import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import VueRecaptcha from 'vue-recaptcha';
import { AxiosError } from 'axios';

import { ErrorMode } from '@/models';
import { makeFormField } from '@/helpers';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { ui } from '@/constants';
import { ErrorEvent } from '@/services/analytics/events';

enum AddDiscountToWalletErrorCode {
  CannotAddPromo = 31,
  BarcodeAlreadyRedeemed = 33,
  OfferIsExpired = 34,
  CodeIsInvalid = 35,
}

@Component({
  name: 'CmgAddRewardModal',
  components: {
    VueRecaptcha,
  },
})
export default class CmgAddRewardModal extends Vue {
  contentScrolled = false;
  messageText = 'Look for a random set of letters or numbers usually found on the back of printed promotions or sent in a text.'
  rewardCode = makeFormField('');
  siteKey = this.$services.config.getRecaptchaKey();
  isLoading = false;
  recaptchaRendered = false;

  get visible(): boolean {
    return this.$appStore.state.ui.modals.addReward.visible;
  }

  get showErrorBuffer(): boolean {
    return !!this.rewardCode.error && this.rewardCode.errorMode == ErrorMode.BelowField;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  onClose(): void {
    this.isLoading = false;
    this.rewardCode = makeFormField('');
    this.$services.ui.setModalVisibility('addReward', false);
    this.$services.ui.focusBack();
  }

  onContentScroll({ target: { scrollTop } }: { target: { scrollTop: number } }): void {
    this.contentScrolled = scrollTop !== 0;
  }

  executeRecaptcha(): void {
    (this.$refs.invisibleRecaptcha as VueRecaptcha).execute();
  }

  resetRecaptcha(): void {
    (this.$refs.invisibleRecaptcha as VueRecaptcha).reset();
  }

  async onRecaptchaVerify(token: string): Promise<void> {
    try {
      this.isLoading = true;

      const addPromoCodeRes = await this.$services.promotions.addPromoCode(this.rewardCode.value, token);
      if (addPromoCodeRes.status === 200) {
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardsLanding,
          action: this.rewardCode.value + AnalyticsEventAction.RewardAdded,
          label: AnalyticsEventLabel.RewardAdded + this.rewardCode.value,
        });
        this.$services.reward.setShouldShowRefresh(false);
        this.$services.promotions.refreshCustomerPromotions();
        this.onClose();
      }
    } catch (e: any) {
      this.resetRecaptcha();
      this.rewardCode.dirty = true;

      // HACK: we should create a custom exception type for promo code failures
      const ex = e as AxiosError;
      if (ex.response && ex.response.status === 400) {
        const error = (ex.response.data as { ErrorCode: number }[])[0];
        if (error) {
          switch (error.ErrorCode) {
            case AddDiscountToWalletErrorCode.CannotAddPromo:
              this.rewardCode.error = 'Add your promo in the bag';
              this.rewardCode.errorMode = ErrorMode.BelowField;
              this.sendAnalyticsOnRewardFail();
              break;
            
            case AddDiscountToWalletErrorCode.BarcodeAlreadyRedeemed:
              this.rewardCode.error = 'Reward already redeemed';
              this.rewardCode.errorMode = ErrorMode.BelowField;
              this.sendAnalyticsOnRewardFail();
              break;

            case AddDiscountToWalletErrorCode.CodeIsInvalid:
              this.rewardCode.error = 'Invalid reward code';
              this.rewardCode.errorMode = ErrorMode.BelowField;
              this.sendAnalyticsOnRewardFail();
              break;

            case AddDiscountToWalletErrorCode.OfferIsExpired:
              this.rewardCode.error = 'Promo has expired';
              this.rewardCode.errorMode = ErrorMode.BelowField;
              this.sendAnalyticsOnRewardFail();
              break;

            default:
              this.rewardCode.error = 'Invalid reward code';
              this.rewardCode.errorMode = ErrorMode.BelowField;
              this.sendAnalyticsOnRewardFail();
              break;
          }
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  onRecaptchaExpired(_: any): void {
    this.resetRecaptcha();
  }

  onRecaptchaRender(): void {
    this.recaptchaRendered = true;
  }

  sendAnalyticsOnRewardFail(): void {
    this.$services.analytics.send(new ErrorEvent('account', this.rewardCode.error ?? '', 'add-reward'));
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: this.rewardCode.error + AnalyticsEventAction.RewardFailure,
      label: AnalyticsEventLabel.RewardFailure + this.rewardCode.error,
    });
  }

  async addReward(): Promise<void> {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: AnalyticsEventAction.AddRewardClick,
      label: AnalyticsEventLabel.AddRewardClick,
    });

    if (!this.rewardCode.value) {
      this.rewardCode.dirty = true;
      this.rewardCode.error = 'Offer Code Required';
      this.rewardCode.errorMode = ErrorMode.PlaceHolder;
    } 
    else {
      this.isLoading = true;
      this.executeRecaptcha();
    }
  }
}
